import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, Redirect, useLocation } from 'react-router-dom';
import classnames from 'classnames';

import logo from '../../_assets/images/logo.svg';
import { useToggle } from '../../_hooks';
import { usePostMessageApi } from '../../_hooks/usePostMessageApi';
import { Button, Timer } from '../../_shared';
import { examinationActions } from '../../_store/actions';
import { authSelectors, examinationSelectors } from '../../_store/selectors';
import { translations } from '../../_translations';
import { isKbcApp } from '../../_utils/initializeHelper';
import { AnswerOptionStatus, isKbcBehaviour, shuffle } from '../../_utils/questionHelpers';
import { Answer } from '../_models/Answer';
import AnswerOption from '../answer-option/AnswerOption';
import ImageCloseUp from '../modal/imgcloseup/ImageCloseUp';
import StopExaminationModal from '../modal/stopexaminationmodal/StopExaminationModal';

const Question: FC = () => {
  const [currentAnswers, setCurrentAnswers] = useState<Answer[]>([]);
  const { pathname } = useLocation();
  const questions = useSelector(examinationSelectors.questions) || [];
  const amountOfQuestions = useSelector(examinationSelectors.amountOfQuestions);
  const currentQuestion = useSelector(examinationSelectors.currentQuestion);
  const isLastQuestion = useSelector(examinationSelectors.isLastQuestion(amountOfQuestions));
  const currentQuestionIndex = questions.indexOf(currentQuestion);
  const selectedAnswer = useSelector(examinationSelectors.answerForQuestion(currentQuestion?.question_id));
  const token = useSelector(authSelectors.token);
  const [isImageCloseOpen, showImageCloseUp] = useToggle(false);
  const [isStopExaminationOpen, setStopExamination] = useToggle(false);
  const dispatch = useDispatch();
  const shouldShowPrompt = !isStopExaminationOpen && !isLastQuestion && pathname === '/examination';
  const { keepAlive } = usePostMessageApi();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (currentQuestion) {
      setCurrentAnswers(shuffle(currentQuestion.answers));
    }
  }, [currentQuestion, currentQuestionIndex]);

  function goToNextQuestion(): void {
    if (isKbcApp()) keepAlive();
    if (isLastQuestion) dispatch(new examinationActions.ShowResult());
    else dispatch(new examinationActions.SelectNextQuestion());
  }

  function answerToStatus(answer: Answer): AnswerOptionStatus {
    if (!selectedAnswer) return AnswerOptionStatus.Unselected;
    if (selectedAnswer.answer_id === answer.answer_id) return AnswerOptionStatus.Selected;
    return AnswerOptionStatus.Unselected;
  }

  if (!questions.length || !currentQuestion || amountOfQuestions < 1) return <Redirect to="/" />;

  return (
    <>
      {isStopExaminationOpen && <StopExaminationModal closeModal={() => setStopExamination(false)} />}
      {isImageCloseOpen && <ImageCloseUp onClose={() => showImageCloseUp(false)} question={currentQuestion} />}
      <Timer question={currentQuestion} />
      <Prompt message={translations.getLabel('QUESTIONS.PROMPT')} when={shouldShowPrompt} />
      <div className={classnames('question-wrapper', { 'is-kbc-behaviour': isKbcBehaviour(token) })}>
        <div className="pinch-zoom-wrapper">
          <img
            alt={translations.getLabel('MODAL.IMAGE_ALT')}
            onClick={() => showImageCloseUp(true)}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = logo;
            }}
            src={currentQuestion.img_url}
          />
        </div>
        <div className="answers-wrapper">
          <p className="info">{`${translations.getLabel('QUESTIONS.QUESTION').toUpperCase()} ${
            currentQuestionIndex + 1
          }/${amountOfQuestions}`}</p>
          <h2>{currentQuestion.question}</h2>
          {currentAnswers.map(answer => (
            <AnswerOption
              answer={answer}
              key={answer.answer_id}
              onSelect={() =>
                dispatch(
                  new examinationActions.SetAnswer({
                    answer: answer,
                    questionId: currentQuestion?.question_id,
                  }),
                )
              }
              status={answerToStatus(answer)}
            />
          ))}
          <div className="actions-wrapper">
            <Button asText onClick={() => setStopExamination(true)}>
              {translations.getLabel('BUTTONS.STOP')}
            </Button>
            <Button onClick={goToNextQuestion} theme={isLastQuestion ? 'primary' : 'neutral'}>
              {translations.getLabel(isLastQuestion ? 'BUTTONS.SHOW_RESULTS' : 'BUTTONS.NEXT')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Question;
